import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { TableColumnConfigDto } from '@asset-metrix/portal-gateway-client-js';
import { GoogleFontIcon, isNotNullOrUndefined } from '@asset-metrix/portal-ui';
import clsx from 'clsx';

import { useCustomTableFilterQuery } from '../../../hooks/filter';
import { useStore } from '../../../store';
import { Filter } from '../Filter';
import { isDualInputRangeFilterDisplay } from '../util';

import styles from '../filters.module.scss';

type ColumnFilterConfig = Pick<TableColumnConfigDto, 'id' | 'customFilter'>;
type CustomTableFiltersProps = {
  activeCustomFilterIds: string[];
  columnConfigs: ColumnFilterConfig[];
  pageFilterInstanceId: string;
  tableFilterInstanceId: string;
  widgetId: string;
};

export const CustomTableFilters = ({
  activeCustomFilterIds,
  columnConfigs,
  pageFilterInstanceId,
  tableFilterInstanceId,
  widgetId,
}: CustomTableFiltersProps) => {
  const { t } = useTranslation();
  const activeFilterConfigs = useMemo(
    () =>
      columnConfigs
        .filter((c) => activeCustomFilterIds.includes(c.id))
        .map((c) => c.customFilter)
        .filter(isNotNullOrUndefined),
    [activeCustomFilterIds, columnConfigs]
  );

  const resetFilterSelections = useStore(
    (state) => state.resetFilterSelections
  );
  const query = useCustomTableFilterQuery(
    widgetId,
    tableFilterInstanceId,
    pageFilterInstanceId,
    activeFilterConfigs
  );

  const isFetching = query.isFetching || !query.isSuccess;

  return activeFilterConfigs.length > 0
    ? activeFilterConfigs.map((c, index) => {
        return (
          <div className={styles['pagefilter__wrapper']} key={c.key}>
            <div
              className={clsx(
                styles['pagefilter__filter'],
                isDualInputRangeFilterDisplay(c.display) &&
                  styles['pagefilter__filter--no-width']
              )}
            >
              <Filter
                filterConfig={c}
                entityId={tableFilterInstanceId}
                isFetching={isFetching}
                filterData={query.validatedData?.[c.key]}
              />
            </div>
            {index + 1 === activeFilterConfigs.length && (
              <GoogleFontIcon
                title={t('app.pageFilter.reset')}
                onClick={() => resetFilterSelections(tableFilterInstanceId)}
                solid={true}
                className={styles['resetIcon']}
                name="filter_alt_off"
              />
            )}
          </div>
        );
      })
    : null;
};
